import { useCallback, useEffect, useRef, useState } from "react";

import { getJson, postJson } from "./money";
import { btn, C, FONT } from "./publicShared";

// Astrologer's desk — opened from the private link VedSanga sends each
// approved astrologer (/?page=astro-desk&id=…&key=…). No login: the key is
// the astrologer's own secret. Shows waiting questions, lets them take one,
// chat for the paid minutes with the person's chart beside it, and see
// what they have earned. English only for now (astrologers are onboarded on a call).

const card = { background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 16, padding: 18, marginBottom: 14 };
const STATUS = { waiting: "Waiting", active: "Chat open", closed: "Finished", refund_requested: "Refund asked", refunded: "Refunded" };
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

export default function AstroDeskPage() {
  const q = new URLSearchParams(window.location.search);
  const aid = q.get("id") || "";
  const key = q.get("key") || "";
  const [desk, setDesk] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    getJson(`/astro-desk/${aid}?key=${encodeURIComponent(key)}`).then((d) => { setDesk(d); setErr(""); }, (e) => setErr(e.message));
  }, [aid, key]);
  useEffect(() => {
    load();
    const h = setInterval(load, 20000);     // also keeps "online now" on for users
    return () => clearInterval(h);
  }, [load]);

  const wrap = (c) => <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px 60px", fontFamily: FONT, color: C.text, background: C.bg, minHeight: "100vh" }}>{c}</div>;
  if (err && !desk) return wrap(<p style={{ fontSize: 19 }}>{err}</p>);
  if (!desk) return wrap(<p style={{ fontSize: 19 }}>Loading your desk…</p>);
  if (openId) return wrap(<DeskChat aid={aid} dkey={key} id={openId} desk={desk} onBack={() => { setOpenId(null); load(); }} />);

  const take = async (id) => {
    try { await postJson(`/astro-desk/${aid}/claim/${id}?key=${encodeURIComponent(key)}`, {}); setOpenId(id); }
    catch (e) { setErr(e.message); load(); }
  };

  return wrap(
    <>
      <h1 style={{ fontSize: 28, fontWeight: 800, margin: "0 0 4px" }}>Namaste, {desk.astrologer.name} 🙏</h1>
      <p style={{ fontSize: 17, color: C.dim, margin: "0 0 18px" }}>
        Keep this page open to show as online. Each consultation is a {desk.minutes}-minute chat from the moment you take it,
        plus {desk.wrapup_minutes} minutes to send a closing summary.
      </p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ ...card, marginBottom: 0, flex: "1 1 200px" }}><div style={{ fontSize: 16, color: C.dim }}>Earned (finished chats)</div><div style={{ fontSize: 28, fontWeight: 800 }}>₹{desk.earned_inr}</div></div>
        <div style={{ ...card, marginBottom: 0, flex: "1 1 200px" }}><div style={{ fontSize: 16, color: C.dim }}>To be paid this week</div><div style={{ fontSize: 28, fontWeight: 800 }}>₹{desk.unpaid_inr}</div></div>
      </div>
      {err && <p style={{ color: "#B42318", fontSize: 17 }}>{err}</p>}

      <h2 style={{ fontSize: 22 }}>Waiting for an astrologer ({desk.waiting.length})</h2>
      {desk.waiting.length === 0 && <p style={{ fontSize: 17, color: C.dim }}>No questions waiting right now. This page refreshes by itself.</p>}
      {desk.waiting.map((x) => (
        <div key={x.id} style={card}>
          <p style={{ fontSize: 18, lineHeight: 1.5, margin: "0 0 10px" }}>{x.question}</p>
          <div style={{ fontSize: 15, color: C.dim, marginBottom: 10 }}>
            Asked {new Date(x.created_at).toLocaleString("en-IN")} · {x.requested_astrologer === "any" ? "open to all astrologers" : "asked for you"}
            {x.birth?.dob ? "" : " · no birth details"}
          </div>
          <button onClick={() => take(x.id)} style={{ ...btn(C.accent), maxWidth: 320 }}>Take this consultation</button>
        </div>
      ))}

      <h2 style={{ fontSize: 22, marginTop: 24 }}>My consultations</h2>
      {desk.mine.length === 0 && <p style={{ fontSize: 17, color: C.dim }}>None yet.</p>}
      {desk.mine.map((x) => (
        <div key={x.id} style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.question}</div>
            <div style={{ fontSize: 15, color: C.dim }}>
              {STATUS[x.status] || x.status}{x.payout_inr ? ` · ₹${x.payout_inr}${x.paid_out ? " paid" : ""}` : ""}{x.rating ? ` · ${x.rating}★` : ""}
            </div>
          </div>
          <button onClick={() => setOpenId(x.id)} style={{ ...btn(C.accent), width: "auto", minHeight: 44 }}>Open</button>
        </div>
      ))}

      <div style={{ ...card, background: C.warm, marginTop: 24, fontSize: 16, lineHeight: 1.6 }}>
        <b>Our promise to users:</b> no fear, no guaranteed predictions, no medical, legal or investment advice,
        no pushing gemstones or costly pujas, keep the conversation on VedSanga (phone numbers and links are hidden automatically),
        and extra care with marriage questions.
      </div>
    </>
  );
}

function DeskChat({ aid, dkey, id, onBack }) {
  const [c, setC] = useState(null);
  const [chart, setChart] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const last = useRef(0);

  const poll = useCallback(() => {
    getJson(`/astro-desk/${aid}/consult/${id}?key=${encodeURIComponent(dkey)}&after=${last.current}`).then((d) => {
      setC(d);
      if (d.chart) setChart(d.chart);
      if (d.messages?.length) {
        last.current = d.messages[d.messages.length - 1].id;
        setMsgs((m) => [...m, ...d.messages]);
      }
    }, (e) => setNote(e.message));
  }, [aid, id, dkey]);
  useEffect(() => { poll(); const h = setInterval(poll, 5000); return () => clearInterval(h); }, [poll]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setText("");
    try { await postJson(`/astro-desk/${aid}/message`, { key: dkey, consult_id: id, body }); poll(); }
    catch (e) { setNote(e.message); setText(body); }
  };
  const close = async () => {
    try { await postJson(`/astro-desk/${aid}/close/${id}?key=${encodeURIComponent(dkey)}`, {}); poll(); }
    catch (e) { setNote(e.message); }
  };
  const minsLeft = c?.ends_at ? Math.ceil((new Date(c.ends_at) - new Date()) / 60000) : null;

  return (
    <>
      <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 17, color: C.accentDark, cursor: "pointer", padding: 0, marginBottom: 14 }}>← Back to desk</button>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "2 1 420px", minWidth: 0 }}>
          {c && (
            <div style={{ ...card, background: C.warm, fontSize: 17 }}>
              <b>{STATUS[c.status] || c.status}</b>
              {c.status === "active" && minsLeft !== null && (minsLeft > 0 ? ` · ${minsLeft} min left` : " · time is up — please send your closing summary")}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "10px 0" }}>
            {msgs.map((m) => (
              <div key={m.id} style={{
                alignSelf: m.sender === "astrologer" ? "flex-end" : "flex-start", maxWidth: "85%", whiteSpace: "pre-wrap",
                background: m.sender === "astrologer" ? "#FFE7CC" : C.card, border: `1.5px solid ${C.border}`, borderRadius: 14,
                padding: "10px 14px", fontSize: 18, lineHeight: 1.5,
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.dim }}>{m.sender === "astrologer" ? "You" : "User"}</div>
                {m.body}
              </div>
            ))}
          </div>
          {c?.can_send && (
            <>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} aria-label="Your reply"
                placeholder="Write your reply" style={{ width: "100%", boxSizing: "border-box", fontSize: 18, padding: 12, borderRadius: 12, border: `1.5px solid ${C.border}`, fontFamily: FONT }} />
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button onClick={send} style={{ ...btn(C.accent), maxWidth: 240 }}>Send</button>
                <button onClick={close} style={{ ...btn("#fff", C.text), maxWidth: 240, border: `1.5px solid ${C.border}` }}>End consultation</button>
              </div>
            </>
          )}
          {note && <p style={{ fontSize: 17, color: C.dim }}>{note}</p>}
        </div>
        <div style={{ ...card, flex: "1 1 280px" }}>
          <h2 style={{ fontSize: 20, margin: "0 0 10px" }}>Birth chart</h2>
          {c?.birth?.dob ? (
            <div style={{ fontSize: 17, lineHeight: 1.7 }}>
              <div>Born {c.birth.dob} {c.birth.time || "(time not given)"} {c.birth.city ? `· ${c.birth.city}` : ""}</div>
              {chart && !chart.error && (
                <>
                  <div>Moon sign: <b>{chart.moon_rashi}</b> · Nakshatra: <b>{chart.nakshatra}</b> (pada {chart.pada})</div>
                  <div>Lagna: <b>{chart.lagna || "needs birth place"}</b></div>
                  <div>Dasha: <b>{cap(chart.maha_dasha)}</b> / {cap(chart.antardasha)} (until {chart.maha_end})</div>
                  <div>Manglik: {chart.manglik ? "yes" : "no"} · Kaal Sarp: {chart.kaal_sarp ? "yes" : "no"}</div>
                  <div>Sade Sati: {chart.sade_sati ? "yes" : "no"} · Dhaiya: {chart.dhaiya ? "yes" : "no"}</div>
                  <div style={{ marginTop: 8, fontSize: 16, color: C.dim }}>
                    {Object.entries(chart.planets || {}).map(([p, v]) => `${cap(p)} ${v.rashi}${v.retro ? " (R)" : ""}`).join(" · ")}
                  </div>
                </>
              )}
            </div>
          ) : <p style={{ fontSize: 17, color: C.dim }}>The user has not added birth details. Ask for date, time and place of birth in the chat.</p>}
        </div>
      </div>
    </>
  );
}
